import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClinicsService } from '../infrastructure/clinics.service';
import { QUERY_STALE_MS } from '@/shared/api/query-stale-time';
import type {
  Clinic,
  CreateClinicInput,
  UpdateClinicInput,
} from '../infrastructure/clinics.service';

const clinicsService = new ClinicsService();

export const clinicKeys = {
  all: ['clinics'] as const,
  detail: (id: string) => ['clinics', id] as const,
};

export function useClinicsList(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: clinicKeys.all,
    queryFn: () => clinicsService.list(),
    enabled: options?.enabled ?? true,
    staleTime: QUERY_STALE_MS.CATALOG,
  });
}

export function useClinic(clinicId: string | undefined) {
  return useQuery({
    queryKey: clinicKeys.detail(clinicId ?? ''),
    queryFn: () => clinicsService.getById(clinicId!),
    enabled: !!clinicId,
    staleTime: QUERY_STALE_MS.CATALOG,
  });
}

export function useCreateClinic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClinicInput) => clinicsService.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clinicKeys.all });
    },
  });
}

export function useUpdateClinic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateClinicInput }) =>
      clinicsService.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clinicKeys.all });
    },
  });
}

export function useDeactivateClinic() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => clinicsService.deactivate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clinicKeys.all });
    },
  });
}

export type { Clinic, CreateClinicInput, UpdateClinicInput };
